/**
 * <ai-label> – visible AI disclosure on product images (Art. 50 Abs. 4 AI Act).
 *
 * All labels share one popup (#ai-label-hint, rendered by snippets/ai-label-hints.liquid).
 * Hover (mouse) or tap/click on the label opens it; the category text comes from
 * <template id="ai-label-hint-{hint}">. The label is its own button next to the card link,
 * so a tap never navigates to the product.
 */

const HINT_ID = 'ai-label-hint';
const GAP = 8;
const EDGE = 16;
const MOBILE_QUERY = window.matchMedia('(max-width: 699px)');

let activeLabel = null;
let closeTimer = null;
let openTimer = null;
let swallowClickUntil = 0;

const hintEl = () => document.getElementById(HINT_ID);

// Height covered by the sticky header, so the popup is never placed underneath it.
// --sticky-area-height is a calc() expression ("calc(0 * 0px + 1 * 62px)"), so it has to be
// resolved by the browser instead of parsed.
let stickyProbe = null;
function stickyTop() {
  if (!stickyProbe) {
    stickyProbe = document.createElement('div');
    stickyProbe.setAttribute('aria-hidden', 'true');
    stickyProbe.style.cssText =
      'position:absolute;visibility:hidden;pointer-events:none;width:0;height:var(--sticky-area-height, 0px)';
    document.body.append(stickyProbe);
  }
  return stickyProbe.offsetHeight || 0;
}

function positionHint(button, hint) {
  const rect = button.getBoundingClientRect();
  const viewportWidth = document.documentElement.clientWidth;

  let left;
  if (MOBILE_QUERY.matches) {
    hint.style.width = `${viewportWidth - EDGE * 2}px`;
    left = EDGE;
  } else {
    hint.style.width = '';
    left = Math.min(rect.left, viewportWidth - hint.offsetWidth - EDGE);
    left = Math.max(left, EDGE);
  }

  const height = hint.offsetHeight;
  const fitsAbove = rect.top - height - GAP >= stickyTop() + EDGE;
  const top = fitsAbove ? rect.top - height - GAP : rect.bottom + GAP;

  hint.style.left = `${left + window.scrollX}px`;
  hint.style.top = `${top + window.scrollY}px`;
  hint.dataset.placement = fitsAbove ? 'top' : 'bottom';
}

function openHint(label, via) {
  const hint = hintEl();
  if (!hint) return;

  clearTimeout(closeTimer);
  clearTimeout(openTimer);

  const template =
    document.getElementById(`ai-label-hint-${label.getAttribute('hint')}`) ||
    document.getElementById('ai-label-hint-rx_generic');
  const body = hint.querySelector('[data-ai-hint-body]');
  if (template && body) body.replaceChildren(template.content.cloneNode(true));

  if (activeLabel && activeLabel !== label) activeLabel.button.setAttribute('aria-expanded', 'false');
  activeLabel = label;
  label.button.setAttribute('aria-expanded', 'true');
  hint.dataset.via = via;

  hint.hidden = false;
  positionHint(label.button, hint);
  requestAnimationFrame(() => hint.classList.add('is-open'));
}

function closeHint({ returnFocus = false } = {}) {
  const hint = hintEl();
  clearTimeout(openTimer);
  clearTimeout(closeTimer);
  if (!hint || !activeLabel) return;

  const label = activeLabel;
  activeLabel = null;
  label.button.setAttribute('aria-expanded', 'false');
  hint.classList.remove('is-open');
  hint.hidden = true;
  if (returnFocus && label.isConnected) label.button.focus();
}

function scheduleClose() {
  clearTimeout(closeTimer);
  closeTimer = setTimeout(() => {
    if (hintEl()?.dataset.via === 'hover') closeHint();
  }, 200);
}

class AiLabel extends HTMLElement {
  connectedCallback() {
    // Bound per element instance, not per attribute: carousels that clone slides get working clones.
    if (this.bound) return;
    this.button = this.querySelector('.ai-label__button');
    if (!this.button) return;
    this.bound = true;
    this.button.setAttribute('aria-controls', HINT_ID);

    // Keep taps and drags away from the card link, carousel and zoom underneath.
    this.button.addEventListener('pointerdown', (event) => event.stopPropagation());
    this.button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (activeLabel === this && hintEl()?.dataset.via === 'click') {
        closeHint();
      } else {
        openHint(this, 'click');
      }
    });

    this.button.addEventListener('pointerenter', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(closeTimer);
      if (activeLabel === this) return;
      clearTimeout(openTimer);
      openTimer = setTimeout(() => openHint(this, 'hover'), 80);
    });

    this.button.addEventListener('pointerleave', (event) => {
      if (event.pointerType !== 'mouse') return;
      clearTimeout(openTimer);
      if (activeLabel === this) scheduleClose();
    });

    // Keyboard: tabbing away from the label closes the popup.
    this.button.addEventListener('focusout', (event) => {
      if (activeLabel !== this) return;
      if (event.relatedTarget && event.relatedTarget.closest?.(`#${HINT_ID}`)) return;
      if (event.relatedTarget) closeHint();
    });
  }

  disconnectedCallback() {
    // Filters, pagination and quick view swap the cards: do not leave the popup floating.
    if (activeLabel === this) closeHint();
  }
}

if (!customElements.get('ai-label')) {
  customElements.define('ai-label', AiLabel);

  // Touch: the first tap next to an open popup only closes it, so it does not also open a product
  // card by accident. Mouse: the click goes through as usual.
  document.addEventListener('pointerdown', (event) => {
    if (!activeLabel) return;
    if (event.target.closest(`#${HINT_ID}, ai-label`)) return;
    if (event.pointerType !== 'mouse' && hintEl()?.dataset.via === 'click') {
      swallowClickUntil = Date.now() + 600;
    }
    closeHint();
  });
  document.addEventListener('pointercancel', () => {
    swallowClickUntil = 0;
  });
  document.addEventListener(
    'click',
    (event) => {
      if (Date.now() > swallowClickUntil) return;
      swallowClickUntil = 0;
      event.preventDefault();
      event.stopPropagation();
    },
    { capture: true },
  );

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && activeLabel) closeHint({ returnFocus: true });
  });

  // Mobile browsers fire resize when the address bar collapses on scroll: only a real width change
  // (rotation, window resize) closes the popup, the scroll handler below keeps it in place otherwise.
  let lastWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    closeHint();
  });

  const hint = hintEl();
  hint?.addEventListener('pointerenter', () => clearTimeout(closeTimer));
  hint?.addEventListener('pointerleave', (event) => {
    if (event.pointerType === 'mouse') scheduleClose();
  });

  // Page scroll: follow the label (the desktop gallery is sticky). Scroll inside a carousel or
  // card list moves the label sideways: close instead.
  let frame = 0;
  document.addEventListener(
    'scroll',
    (event) => {
      if (!activeLabel) return;
      const target = event.target;
      if (target !== document && target !== document.documentElement && target.contains?.(activeLabel)) {
        closeHint();
        return;
      }
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const current = hintEl();
        if (activeLabel && current && !current.hidden) positionHint(activeLabel.button, current);
      });
    },
    { capture: true, passive: true },
  );
}
